const _cpEN = typeof window!=='undefined' && window.location.hostname==='cad.retaliq.net'
/**
 * ChatPanel — Asistente IA integrado en el CAD
 *
 * Panel deslizable desde el lado derecho. El asistente conoce el contexto
 * del operador (vista activa, sitio seleccionado) y puede consultar:
 * incidentes, LPR, estado de sitios, detecciones y estadísticas.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'

// ─── Sugerencias rápidas ──────────────────────────────────────────────────────

const SUGGESTIONS = [
  '¿Qué incidentes están abiertos ahora?',
  _cpEN ? 'Which sites have active alarms?' : '¿Qué sitios tienen alarmas activas?',
  '¿Cuántos eventos tuvimos en las últimas 8 horas?',
  'Dame el resumen del turno actual',
  '¿Algún sitio está offline?',
]

// ─── Renderizador de markdown simple ─────────────────────────────────────────

function SimpleMarkdown({ text }) {
  const lines = text.split('\n')
  const elements = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Encabezados
    if (line.startsWith('### ')) {
      elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 12, marginTop: 10, marginBottom: 4, color: 'var(--accent)' }}>{line.slice(4)}</div>)
    } else if (line.startsWith('## ')) {
      elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: 10, marginBottom: 4 }}>{line.slice(3)}</div>)
    } else if (line.startsWith('# ')) {
      elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 14, marginTop: 8, marginBottom: 4 }}>{line.slice(2)}</div>)
    }
    // Lista con - o *
    else if (line.match(/^[-*] /)) {
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      )
    }
    // Lista numerada
    else if (line.match(/^\d+\. /)) {
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: 'var(--text-muted)', flexShrink: 0, minWidth: 16 }}>{line.match(/^\d+/)[0]}.</span>
          <span>{renderInline(line.replace(/^\d+\. /, ''))}</span>
        </div>
      )
    }
    // Línea en blanco
    else if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: 6 }} />)
    }
    // Párrafo normal
    else {
      elements.push(<div key={i} style={{ marginBottom: 2, lineHeight: 1.5 }}>{renderInline(line)}</div>)
    }
    i++
  }

  return <div style={{ fontSize: 12 }}>{elements}</div>
}

function renderInline(text) {
  // Bold **text**
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} style={{
        background: 'var(--bg-muted)', padding: '1px 4px',
        borderRadius: 3, fontFamily: 'monospace', fontSize: 11,
      }}>{part.slice(1, -1)}</code>
    }
    return part
  })
}

// ─── Mensaje individual ───────────────────────────────────────────────────────

// ─── Panel de hits forenses (thumbnails + deep-link a galería) ───────────────

function ForensicHitsPanel({ forensic }) {
  const { hits, grants, filters, input } = forensic
  const [lightbox, setLightbox] = useState(null)

  // URL del crop usando el grant del sitio. crop.jpg para thumbnail,
  // frame.jpg para el lightbox.
  const buildUrl = (hit, kind = 'crop') => {
    const grant = grants[String(hit.site_id)]
    if (!grant || !hit.sensor_uuid) return null
    return `${grant.base_url}/sensors/${hit.sensor_uuid}/${kind}.jpg`
         + `?token=${encodeURIComponent(grant.token)}`
  }

  // Deep-link a la galería forense con los mismos filtros aplicados.
  // ForensicGallery acepta query params en su URL.
  const galleryHref = (() => {
    const p = new URLSearchParams()
    p.set('tab', 'forensic')
    if (input?.site_name) p.set('site_name', input.site_name)
    if (filters?.camera_name) p.set('camera_name', filters.camera_name)
    if (filters?.hours_back) p.set('hours', String(filters.hours_back))
    if (input?.object_kind)  p.set('object_kind', input.object_kind)
    if (input?.vehicle_type) p.set('vehicle_type', input.vehicle_type)
    if (input?.color)        p.set('color', input.color)
    if (input?.make)         p.set('make', input.make)
    if (input?.plate)        p.set('plate', input.plate)
    return `/monitor?${p}`
  })()

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.replace(' ', 'T') + 'Z')
      return d.toLocaleTimeString('es-CL',
        { hour: '2-digit', minute: '2-digit' })
    } catch { return ts.slice(11, 16) }
  }

  return (
    <div style={{ marginTop: 8 }}>
      {/* Botón abrir galería con filtros */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)',
                       fontWeight: 600, letterSpacing: '0.04em' }}>
          {hits.length} HALLAZGO{hits.length !== 1 ? 'S' : ''}
        </span>
        <a href={galleryHref} target="_blank" rel="noopener noreferrer"
           style={{
             fontSize: 10, fontWeight: 600, padding: '3px 8px',
             borderRadius: 4, background: 'var(--accent)', color: '#fff',
             textDecoration: 'none',
           }}>
          🔍 Abrir en galería →
        </a>
      </div>

      {/* Grilla de thumbnails clickeables (max 12 mostrados) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: 4,
      }}>
        {hits.slice(0, 12).map((h, i) => {
          const url = buildUrl(h, 'crop')
          if (!url) return null
          return (
            <button
              key={h.id || i}
              onClick={() => setLightbox(h)}
              title={`${h.camera} · ${fmtTime(h.ts)} · ${h.vehicle_type || h.event_type}${h.color ? ' ' + h.color : ''}${h.plate ? ' · ' + h.plate : ''}`}
              style={{
                position: 'relative', padding: 0, border: 'none',
                background: '#000', borderRadius: 4, overflow: 'hidden',
                cursor: 'pointer', aspectRatio: '1/1',
              }}>
              <img src={url} alt={h.camera}
                onError={(e) => { e.target.style.display = 'none' }}
                style={{ width: '100%', height: '100%', objectFit: 'cover',
                         display: 'block' }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                color: '#fff', fontSize: 8, padding: '6px 3px 2px',
                textAlign: 'center', fontWeight: 600,
              }}>
                {fmtTime(h.ts)}
              </div>
            </button>
          )
        })}
      </div>
      {hits.length > 12 && (
        <div style={{ fontSize: 9, color: 'var(--text-muted)',
                      textAlign: 'center', marginTop: 4 }}>
          +{hits.length - 12} más en la galería
        </div>
      )}

      {/* Lightbox del frame completo */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 500,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh',
                        display: 'flex', flexDirection: 'column', gap: 8 }}>
            <img src={buildUrl(lightbox, 'frame')}
                 alt="frame"
                 style={{ maxWidth: '100%', maxHeight: '85vh',
                          objectFit: 'contain', borderRadius: 6 }} />
            <div style={{ color: '#fff', fontSize: 12, textAlign: 'center' }}>
              <strong>{lightbox.camera}</strong> · {fmtTime(lightbox.ts)}
              {lightbox.vehicle_type && ` · ${lightbox.vehicle_type}`}
              {lightbox.color && ` · ${lightbox.color}`}
              {lightbox.make && ` · ${lightbox.make}`}
              {lightbox.plate && ` · ${lightbox.plate}`}
              {lightbox.confidence && ` · ${Math.round(lightbox.confidence*100)}%`}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Message({ msg }) {
  const isUser = msg.role === 'user'
  const isErr  = msg.role === 'error'

  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <div style={{
          maxWidth: '80%', padding: '8px 12px', borderRadius: '12px 12px 2px 12px',
          background: 'var(--accent)', color: '#fff', fontSize: 12,
        }}>
          {msg.content}
        </div>
      </div>
    )
  }

  if (isErr) {
    return (
      <div style={{
        padding: '8px 12px', borderRadius: 8, marginBottom: 8,
        background: '#fef2f2', border: '1px solid #fecaca',
        color: '#dc2626', fontSize: 11,
      }}>
        ⚠️ {msg.content}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: '#fff', flexShrink: 0, fontWeight: 700, marginTop: 2,
      }}>A</div>
      <div style={{
        flex: 1, padding: '8px 12px', borderRadius: '2px 12px 12px 12px',
        background: 'var(--bg-muted)', border: '1px solid var(--border)',
        color: 'var(--text-primary)',
      }}>
        <SimpleMarkdown text={msg.content} />
        {msg.forensic && <ForensicHitsPanel forensic={msg.forensic} />}
      </div>
    </div>
  )
}

// ─── ChatPanel ────────────────────────────────────────────────────────────────

export default function ChatPanel({ open, onClose, context }) {
  const token   = useAuthStore(s => s.token)
  const [input,    setInput]    = useState('')
  const [messages, setMessages] = useState([])
  const [loading,  setLoading]  = useState(false)
  const bottomRef  = useRef(null)
  const inputRef   = useRef(null)

  // Scroll al fondo
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Focus al abrir
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100)
  }, [open])

  const send = useCallback(async (text) => {
    const userText = (text || input).trim()
    if (!userText || loading) return

    setInput('')
    const newMessages = [...messages, { role: 'user', content: userText }]
    setMessages(newMessages)
    setLoading(true)

    try {
      const res = await fetch('/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: newMessages.filter(m => m.role !== 'error'),
          context,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Error ${res.status}`)
      }

      const data = await res.json()
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.answer,
        // Si el chat usó search_forensic, embebemos hits + grants en el msg
        // para que el render muestre thumbnails y botón "Abrir en galería".
        forensic: data.forensic_hits?.length ? {
          hits:    data.forensic_hits,
          grants:  data.forensic_grants || {},
          filters: data.forensic_filters || {},
          input:   data.forensic_input || {},
        } : null,
      }])
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: err.message }])
    } finally {
      setLoading(false)
    }
  }, [input, messages, loading, token, context])

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const clear = () => { setMessages([]); setInput('') }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Overlay para cerrar */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 400, zIndex: 100,
        background: 'var(--bg-surface)', borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        boxShadow: open ? '-8px 0 32px rgba(0,0,0,0.2)' : 'none',
      }}>

        {/* Header */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: '#fff', fontWeight: 700,
          }}>A</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{_cpEN ? 'CAD Assistant' : 'Asistente CAD'}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              Claude Haiku · Acceso a datos en tiempo real
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {messages.length > 0 && (
              <button
                onClick={clear}
                title="Limpiar conversación"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 12, padding: '4px 8px',
                  borderRadius: 6,
                }}
              >Nueva</button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 18, padding: '0 4px',
              }}
            >×</button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>

          {/* Estado vacío */}
          {messages.length === 0 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24, marginTop: 16 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Asistente de operaciones
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Consulta incidentes, historial de patentes,<br />
                  estado de sitios y estadísticas en tiempo real.
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                SUGERENCIAS
              </div>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '8px 12px',
                    borderRadius: 8, border: '1px solid var(--border)',
                    background: 'var(--bg-root)', color: 'var(--text-secondary)',
                    fontSize: 12, cursor: 'pointer', marginBottom: 6,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.target.style.background = 'var(--bg-muted)'}
                  onMouseLeave={e => e.target.style.background = 'var(--bg-root)'}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Mensajes */}
          {messages.map((msg, i) => <Message key={i} msg={msg} />)}

          {/* Indicador de escritura */}
          {loading && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#fff', flexShrink: 0, fontWeight: 700,
              }}>A</div>
              <div style={{
                padding: '10px 14px', borderRadius: '2px 12px 12px 12px',
                background: 'var(--bg-muted)', border: '1px solid var(--border)',
                display: 'flex', gap: 4, alignItems: 'center',
              }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: 'var(--text-muted)',
                    animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: 12, borderTop: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end',
            background: 'var(--bg-root)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '8px 12px',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={_cpEN ? "Ask something… (Enter to send)" : "Pregunta algo… (Enter para enviar)"}
              disabled={loading}
              rows={1}
              style={{
                flex: 1, border: 'none', background: 'transparent',
                color: 'var(--text-primary)', fontSize: 12, resize: 'none',
                outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
                maxHeight: 100, overflowY: 'auto',
              }}
              onInput={e => {
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'
              }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{
                width: 28, height: 28, borderRadius: '50%', border: 'none',
                background: input.trim() && !loading ? 'var(--accent)' : 'var(--bg-muted)',
                color: input.trim() && !loading ? '#fff' : 'var(--text-muted)',
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                fontSize: 14, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s',
              }}
            >↑</button>
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>
            Enter para enviar · Shift+Enter para salto de línea
          </div>
        </div>
      </div>

      {/* CSS para animación de puntos */}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  )
}